import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog } from '@mui/material';

import { clearValidationErrors, createPlacement } from '../../../../../store/recruitmentService';
import {
    getConfiguredItems,
    getNameFromId,
    getObjFromId
} from '../../../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_BUSINESS_NAME,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE
} from '../../../../../utils/formValidation/commonConstants';
import { formatFloat } from '../../../../../utils/numberUtils';
import { clearKeys } from '../../../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import DateSelect from '../../../../formElements/DateSelect';
import RadioButtons from '../../../../formElements/RadioButtons';
import SingleSelect from '../../../../formElements/SingleSelect';
import Switch from '../../../../formElements/Switch';
import TextInputField from '../../../../formElements/TextInputField';
import { validate } from '../validation/validateAddOwnPlacement';

import '../../../../../commonStyles/muiStyles.css';
import form from '../../../../../commonStyles/formStyles.module.css';
import modal from '../../../../../commonStyles/modal.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const adviserRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const AddOwnPlacementEditor = ({ isOpen, openOwnPlacementEditor }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        businessName: '',
        city: '',
        claimableEvent: true,
        employmentTypeId: '',
        fixedFlexibleHour: 'fixed',
        fullServiceDeclined: false,
        hiringManagerEmail: '',
        hiringManagerId: '',
        hiringManagerName: '',
        hiringManagerOfficeNumber: '',
        hiringManagerMobileNumber: '',
        hourlyPay: '',
        hoursPerWeek: null, // text input for fixed
        hoursPerWeekId: '', // selector for flexible
        jobSectorId: '',
        jobStartEvidence: false,
        leadAdviserId: '',
        leadGeneratedById: '',
        participantId: '',
        postcode: '',
        selfEmploymentClaimCalculationDate: '',
        startDate: '',
        vacancyId: '',
        vacancyTitle: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [isLeadAdviser, setIsLeadAdviser] = useState(false);
    const [isSelfEmployment, setIsSelfEmployment] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [arrayPlacementEmploymentTypes, setArrayPlacementEmploymentTypes] = useState([]);
    const [advisers, setAdvisers] = useState([]);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [keys, setKeys] = useState({
        employmentTypeId: '0',
        jobSectorId: '1',
        hoursPerWeekId: '2'
    });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const users = useSelector((state) => state.entities.userService.users);
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const ownPlacementDetails = useSelector(
        (state) => state.entities.directusService.ownPlacementDetails
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const validationErrors = useSelector(
        (state) => state.entities.recruitmentService.validationErrors
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (placementEmploymentTypeDetails?.length < 1 || !currentParticipant?.contractId) return;
        setArrayPlacementEmploymentTypes(
            getConfiguredItems(placementEmploymentTypeDetails, currentParticipant?.contractId)
        );
    }, [placementEmploymentTypeDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (users?.length < 1) return;
        setAdvisers(
            users
                .filter((el) => el.userTypes?.find((entry) => adviserRoles.includes(entry.role)))
                .map((el) => ({ id: el.id, name: el.loginEmailAddress }))
                .sort((a, b) => a.name.localeCompare(b.name))
        );
    }, [users]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.startsWith(`Job placement has been added`)) {
            setIsUpdateButtonDisabled(false);
            clearData();
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const clearData = () => {
        setKeys(clearKeys(keys));
        setNewEntry(initialState);
        setFirstName('');
        setLastName('');
        setAdvisers([]);
        setIsLeadAdviser(false);
        setIsSelfEmployment(false);
        openOwnPlacementEditor(false);
        setErrors({});
        isUpdateButtonDisabled && setIsUpdateButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onEmploymentTypeChange = (value) => {
        onUpdate('employmentTypeId', value);
        if (getNameFromId(placementEmploymentTypeDetails, value) !== 'Self Employed') {
            clearError('selfEmploymentClaimCalculationDate');
            setIsSelfEmployment(false);
            setNewEntry((prev) => ({
                ...prev,
                selfEmploymentClaimCalculationDate: ''
            }));
        } else setIsSelfEmployment(true);
    };

    const onSwitchFixedOrFlexible = (e) => {
        clearError('hoursPerWeek');
        clearError('hoursPerWeekId');
        const { checked } = e.target;
        checked
            ? setNewEntry((prev) => ({
                  ...prev,
                  hoursPerWeek: null,
                  fixedFlexibleHour: 'flexible'
              }))
            : setNewEntry((prev) => ({ ...prev, hoursPerWeekId: '', fixedFlexibleHour: 'fixed' }));
    };

    const onLeadGeneratedByChange = (chosenId) => {
        onUpdate('leadGeneratedById', chosenId);
        if (getNameFromId(ownPlacementDetails, chosenId) !== 'Adviser') {
            setIsLeadAdviser(false);
            clearError('leadAdviserId');
            setNewEntry((prev) => ({ ...prev, leadAdviserId: '' }));
        } else setIsLeadAdviser(true);
    };

    const onFirstNameChange = (value) => {
        clearError('firstName');
        setFirstName(value);
    };

    const onLastNameChange = (value) => {
        clearError('lastName');
        setLastName(value);
    };

    const onClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setNewEntry(initialState);
        setErrors({});
        onCancel();
    };

    const onCancel = () => clearData();

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, isLeadAdviser, isSelfEmployment, firstName, lastName);

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id,
            hiringManagerName: `${firstName?.toLowerCase()} ${lastName?.toLowerCase()}`
        };
        const msg = `Job placement has been added: ${payload.vacancyTitle}`;
        dispatch(createPlacement(payload, msg));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onClose}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader}>
                <h3>Create Own Placement</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="add-own-placement-edit-form">
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="employmentTypeId"
                                key={keys.employmentTypeId}
                                label="Placement Employment Type"
                                placeholder="Select placement employment type..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                menuItems={arrayPlacementEmploymentTypes}
                                selectedId={newEntry.employmentTypeId || ''}
                                selected={getObjFromId(
                                    arrayPlacementEmploymentTypes,
                                    newEntry?.employmentTypeId
                                )}
                                error={errors?.employmentTypeId}
                                onChange={(chosenId) => onEmploymentTypeChange(chosenId)}
                            />
                            <DateSelect
                                id="startDate"
                                label="Start Date"
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                value={newEntry.startDate || ''}
                                error={errors.startDate}
                                onDateChange={(date) => onUpdate('startDate', date)}
                            />
                            <div hidden={!isSelfEmployment}>
                                <DateSelect
                                    id="selfEmploymentClaimCalculationDate"
                                    label="Self Employed Claim Calculation Date"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={!!isSelfEmployment}
                                    value={newEntry.selfEmploymentClaimCalculationDate || ''}
                                    error={errors.selfEmploymentClaimCalculationDate}
                                    onDateChange={(date) =>
                                        onUpdate('selfEmploymentClaimCalculationDate', date)
                                    }
                                />
                            </div>

                            <TextInputField
                                id="vacancyTitle"
                                label="Vacancy Title"
                                placeholder="Enter vacancy title"
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                maxLength={MAX_NAME}
                                value={newEntry.vacancyTitle || ''}
                                error={errors.vacancyTitle}
                                onChange={(e) => onUpdate('vacancyTitle', e.target.value)}
                            />

                            <TextInputField
                                id="hourlyPay"
                                label="Hourly Pay"
                                placeholder="Enter hourly pay"
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                maxLength={6}
                                value={newEntry.hourlyPay || ''}
                                error={errors.hourlyPay}
                                onChange={(e) => onUpdate('hourlyPay', formatFloat(e.target.value))}
                            />

                            <Switch
                                id="fixedFlexibleHour"
                                label="Fixed or Flexible"
                                leftLabel="Fixed"
                                rightLabel="Flexible"
                                disabled={!hasRole(acceptedRoles, roles)}
                                checked={newEntry.fixedFlexibleHour === 'flexible'}
                                onChange={onSwitchFixedOrFlexible}
                            />

                            {newEntry.fixedFlexibleHour === 'fixed' ? (
                                <TextInputField
                                    id="hoursPerWeek"
                                    label="Hours per Week"
                                    placeholder="Enter hours per week"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={newEntry.fixedFlexibleHour === 'fixed'}
                                    maxLength={5}
                                    value={newEntry.hoursPerWeek || ''}
                                    error={errors.hoursPerWeek}
                                    onChange={(e) =>
                                        onUpdate('hoursPerWeek', formatFloat(e.target.value))
                                    }
                                />
                            ) : (
                                <SingleSelect
                                    id="hoursPerWeekId"
                                    key={keys.hoursPerWeekId}
                                    label="Hours per Week"
                                    placeholder="Select hours per week..."
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={newEntry.fixedFlexibleHour === 'flexible'}
                                    menuItems={hoursPerWeekDetails}
                                    selectedId={newEntry.hoursPerWeekId || ''}
                                    selected={getObjFromId(
                                        hoursPerWeekDetails,
                                        newEntry.hoursPerWeekId
                                    )}
                                    error={errors.hoursPerWeekId}
                                    onChange={(chosenId) => onUpdate('hoursPerWeekId', chosenId)}
                                />
                            )}
                            <RadioButtons
                                id="claimableEvent"
                                label="Claimable Event?"
                                disabled={!hasRole(acceptedRoles, roles)}
                                value={newEntry.claimableEvent ?? true}
                                onChange={(option) => onUpdate('claimableEvent', option)}
                            />

                            <SingleSelect
                                id="jobSectorId"
                                key={keys.jobSectorId}
                                label="Job Sector"
                                placeholder="Select a job sector..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                menuItems={jobSectorDetails}
                                selectedId={newEntry.jobSectorId || ''}
                                selected={getObjFromId(jobSectorDetails, newEntry.jobSectorId)}
                                error={errors.jobSectorId}
                                onChange={(chosenId) => onUpdate('jobSectorId', chosenId)}
                            />

                            <TextInputField
                                id="businessName"
                                label="Business Name"
                                placeholder="Enter business name"
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                maxLength={MAX_BUSINESS_NAME}
                                value={newEntry.businessName || ''}
                                error={errors.businessName}
                                onChange={(e) => onUpdate('businessName', e.target.value)}
                            />

                            <SingleSelect
                                id="leadGeneratedById"
                                label="Lead Generated By"
                                placeholder="Select a lead..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                menuItems={ownPlacementDetails}
                                selectedId={newEntry.leadGeneratedById}
                                selected={getObjFromId(
                                    ownPlacementDetails,
                                    newEntry.leadGeneratedById
                                )}
                                error={errors.leadGeneratedById}
                                onChange={onLeadGeneratedByChange}
                            />
                            <div hidden={!isLeadAdviser}>
                                <SingleSelect
                                    id="leadAdviserId"
                                    label="Adviser"
                                    placeholder="Select an adviser..."
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={isLeadAdviser}
                                    menuItems={advisers}
                                    selectedId={newEntry.leadAdviserId}
                                    selected={getObjFromId(advisers, newEntry.leadAdviserId)}
                                    error={errors.leadAdviserId}
                                    onChange={(chosenId) => onUpdate('leadAdviserId', chosenId)}
                                />
                            </div>
                        </div>
                        <div className={form.formColumn}>
                            <TextInputField
                                id="firstName"
                                label="First Name"
                                placeholder="Enter hiring manager first name"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={MAX_NAME}
                                mandatory={true}
                                value={firstName || ''}
                                error={errors.firstName}
                                onChange={(e) => onFirstNameChange(e.target.value)}
                            />
                            <TextInputField
                                id="lastName"
                                label="Last Name"
                                placeholder="Enter hiring manager last name"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={MAX_NAME}
                                mandatory={true}
                                value={lastName || ''}
                                error={errors.lastName}
                                onChange={(e) => onLastNameChange(e.target.value)}
                            />

                            <TextInputField
                                id="hiringManagerOfficeNumber"
                                label="Office Number"
                                placeholder="Enter hiring manager office phone number"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={MAX_PHONE}
                                mandatory={true}
                                value={newEntry.hiringManagerOfficeNumber || ''}
                                error={errors.hiringManagerOfficeNumber}
                                onChange={(e) =>
                                    onUpdate('hiringManagerOfficeNumber', e.target.value)
                                }
                            />
                            <TextInputField
                                id="hiringManagerMobileNumber"
                                label="Mobile Number"
                                placeholder="Enter hiring manager mobile phone number"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={MAX_PHONE}
                                value={newEntry.hiringManagerMobileNumber || ''}
                                error={errors.hiringManagerMobileNumber}
                                onChange={(e) =>
                                    onUpdate('hiringManagerMobileNumber', e.target.value)
                                }
                            />
                            <TextInputField
                                id="hiringManagerEmail"
                                label="Email"
                                placeholder="Enter hiring manager email address"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={MAX_EMAIL}
                                value={newEntry.hiringManagerEmail || ''}
                                error={errors.hiringManagerEmail}
                                onChange={(e) => onUpdate('hiringManagerEmail', e.target.value)}
                            />

                            <TextInputField
                                id="addressLine1"
                                label="Address Line 1"
                                placeholder="Enter address line 1"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                                mandatory={true}
                                value={newEntry.addressLine1 || ''}
                                error={errors.addressLine1}
                                onChange={(e) => onUpdate('addressLine1', e.target.value)}
                            />
                            <TextInputField
                                id="addressLine2"
                                label="Address Line 2"
                                placeholder="Enter address line 2"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                                value={newEntry.addressLine2 || ''}
                                error={errors.addressLine2}
                                onChange={(e) => onUpdate('addressLine2', e.target.value)}
                            />
                            <TextInputField
                                id="addressLine3"
                                label="Address Line 3"
                                placeholder="Enter address line 3"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                                value={newEntry.addressLine3 || ''}
                                error={errors.addressLine3}
                                onChange={(e) => onUpdate('addressLine3', e.target.value)}
                            />
                            <TextInputField
                                id="city"
                                label="City"
                                placeholder="Enter city"
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={CITY_MAX_LENGTH}
                                mandatory={true}
                                value={newEntry.city || ''}
                                error={errors.city}
                                onChange={(e) => onUpdate('city', e.target.value)}
                            />

                            <TextInputField
                                id="postcode"
                                label="Postcode"
                                placeholder="Enter postcode"
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                maxLength={MAX_POSTCODE}
                                value={newEntry.postcode || ''}
                                error={errors.postcode}
                                onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
                            />
                        </div>
                    </div>
                    <div className={modal.actionButtons}>
                        <Button
                            id="updateAddOwnPlacementButton"
                            disabled={!hasRole(acceptedRoles, roles) || isUpdateButtonDisabled}
                            content="Create"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="updateAddOwnPlacementCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

AddOwnPlacementEditor.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    openOwnPlacementEditor: PropTypes.func
};

export default AddOwnPlacementEditor;
